import { lazy } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router';
import { AppLayout } from '@/common/components/shared/layout/app-layout';
import { AuthLayout } from '@/common/components/shared/layout/auth-layout';
import { ProtectedRoute } from '@/common/components/shared/layout/routes/ProtectedRoute';
import { NotFound } from '@/common/components/shared/not-found';
import { Dashboard, GoogleOfflineOAuthCallback, Login, SignInCallback } from './pages';

// --- Lazy Loaded Components ---
const EmployeeList = lazy(() => import('./pages/employees/list/EmployeeList.tsx'));
const EmployeeEdit = lazy(() => import('./pages/employees/edit/EmployeeEdit.tsx'));
const EmployeeEditDefaultRoute = lazy(() => import('./pages/employees/edit/EmployeeEditDefaultRoute.tsx'));
const EmployeeAdd = lazy(() => import('./pages/employees/add/EmployeeAdd.tsx'));
const EmployeeAlertList = lazy(() => import('./pages/employees/alert/EmployeeAlertList.tsx'));
const ExternalAccountsPage = lazy(() => import('./pages/employees/external-accounts/ExternalAccountsPage.tsx'));
const TeamNames = lazy(() => import('./pages/employees/team-names/TeamNames.tsx'));
const TeamNameHistory = lazy(() => import('./pages/employees/team-names/job-history/TeamNameHistory.tsx'));
const OrganizationChart = lazy(() => import('./pages/employees/chart/OrganizationChart.tsx'));
const ProbationReviewList = lazy(() => import('./pages/probation-review/ProbationReviewList.tsx'));
const ProbationReviewAuditLogList = lazy(
  () => import('./pages/probation-review/audit-log/ProbationReviewAuditLogList.tsx'),
);
const PerformanceReviewList = lazy(() => import('./pages/performance-review/list/PerformanceReviewList.tsx'));
const PerformanceReviewCreate = lazy(() => import('./pages/performance-review/create/PerformanceReviewCreatePage.tsx'));
const PerformanceReviewEdit = lazy(() => import('./pages/performance-review/edit/PerformanceReviewEdit.tsx'));
const PendingChangesPage = lazy(() => import('./pages/employees/pending-changes/PendingManagerChangesList.tsx'));
const PerformanceReviewEditDefaultRoute = lazy(
  () => import('./pages/performance-review/edit/PerformanceReviewEditDefaultRoute.tsx'),
);
const PerformanceReviewResultList = lazy(
  () => import('./pages/performance-review/result/PerformanceReviewResultList.tsx'),
);
const NumberOfEmployees = lazy(() => import('./pages/report/number-of-employees/NumberOfEmployees.tsx'));
const PerformanceReviewReport = lazy(() => import('./pages/report/performance-review/PerformanceReviewReport.tsx'));
const ManagerialAccountingReport = lazy(
  () => import('./pages/report/managerial-accounting-report/ManagerialAccountingReport.tsx'),
);
const DemographicReport = lazy(() => import('./pages/report/demographic-report/DemographicReport.tsx'));
const SettingPerformanceReview = lazy(() => import('./pages/setting/performance-review/PerformanceReview.tsx'));
const SettingNotification = lazy(() => import('./pages/setting/notification/NotificationSetting.tsx'));
const SettingGeneralList = lazy(() => import('./pages/setting/general/GeneralList.tsx'));
const MyDetailRedirect = lazy(() => import('./pages/my/MyDetailRedirect.tsx'));
const MyPerformanceReviewRedirect = lazy(() => import('./pages/my/MyPerformanceReviewRedirect.tsx'));
const McpConnectionsPage = lazy(() => import('./pages/my/mcp-connections/McpConnectionsPage.tsx'));
const McpConnectionsSettingPage = lazy(() => import('./pages/setting/mcp-connections/McpConnectionsSettingPage.tsx'));
const OrgHierarchyRelationListPage = lazy(
  () => import('./pages/setting/org-hierarchy-relation/OrgHierarchyRelationListPage.tsx'),
);
const OrgHierarchyRelationAddPage = lazy(
  () => import('./pages/setting/org-hierarchy-relation/OrgHierarchyRelationAddPage.tsx'),
);
const SettingAIAssistant = lazy(() => import('./pages/setting/ai-assistant/AIAssistant.tsx'));
const SettingAIAssistantFormPage = lazy(() => import('./pages/setting/ai-assistant/AIAssistantFormPage.tsx'));
const IdpList = lazy(() => import('./pages/idp/list/IdpList.tsx'));
const IdpDetail = lazy(() => import('./pages/idp/detail/IdpDetail.tsx'));

// --- End Lazy Loaded Components ---

export const router = () =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route element={<NotFound />} path="*" />

        <Route element={<AppLayout />}>
          <Route element={<ProtectedRoute redirectPath="/login" />}>
            <Route element={<Navigate to="/dashboard" />} path="/" />
            <Route element={<Dashboard />} path="/dashboard" />
            <Route element={<Navigate to="/employees/list" />} path="/employees" />
            <Route element={<EmployeeList />} path="/employees/list" />
            <Route element={<OrganizationChart />} path="/employees/organization-chart/:id?" />
            <Route element={<EmployeeEditDefaultRoute />} path="/employees/edit/:id" />
            <Route element={<EmployeeEdit />} path="/employees/edit/:id/:tab" />
            <Route element={<ProtectedRoute allowedFlag="employeeAdd" redirectPath="/" />}>
              <Route element={<EmployeeAdd />} path="/employees/add" />
            </Route>
            <Route element={<ProtectedRoute allowedFlag="employeeAlertListView" redirectPath="/employees" />}>
              <Route element={<EmployeeAlertList />} path="/employees/alert" />
            </Route>
            <Route element={<ProtectedRoute allowedFlag="employeePendingChangesListView" redirectPath="/employees" />}>
              <Route element={<PendingChangesPage />} path="/employees/pending-changes" />
            </Route>
            <Route element={<ProtectedRoute allowedFlag="employeeExternalAccountListView" redirectPath="/employees" />}>
              <Route element={<ExternalAccountsPage />} path="/employees/external-accounts" />
            </Route>
            {/* 2026-09-15 (research.md #36, spec.md FR-019, FR-079 through FR-081): widened from
                SUPER_ADMIN-only to also admit ADMIN_PRIVATE - Country-scoped access is enforced
                server-side on every read/write, not by this route gate, which only answers "can
                this role ever reach the screen at all" (an ADMIN_PRIVATE with zero accessible
                Countries still reaches it and sees an empty Country-selection list, FR-081). */}
            <Route
              element={<ProtectedRoute allowedFlag={['isSuperAdmin', 'isAdminPrivate']} redirectPath="/employees" />}
            >
              <Route element={<TeamNames />} path="/employees/team-names" />
              <Route element={<TeamNameHistory />} path="/employees/team-names/job-history" />
            </Route>

            <Route element={<Navigate to="/performance-review/list" />} path="/performance-review" />
            <Route element={<PerformanceReviewList />} path="/performance-review/list" />
            <Route element={<PerformanceReviewCreate />} path="/performance-review/create" />
            <Route element={<PerformanceReviewEditDefaultRoute />} path="/performance-review/edit/:id" />
            <Route element={<PerformanceReviewEdit />} path="/performance-review/edit/:id/:tab" />
            <Route element={<ProtectedRoute allowedFlag="performanceReviewResultsList" redirectPath="/" />}>
              <Route element={<PerformanceReviewResultList />} path="/performance-review/results" />
            </Route>

            <Route element={<Navigate to="/probation-review/list" />} path="/probation-review" />
            <Route element={<ProbationReviewList />} path="/probation-review/list" />
            <Route element={<ProtectedRoute allowedFlag="probationReviewManage" redirectPath="/probation-review" />}>
              <Route element={<ProbationReviewAuditLogList />} path="/probation-review/audit-log" />
            </Route>

            {/* The signed-in employee's own AI app connections: any employee may open it */}
            <Route element={<MyDetailRedirect />} path="/my/detail/:tab?" />
            <Route element={<MyPerformanceReviewRedirect />} path="/my/performance-review" />
            <Route element={<McpConnectionsPage />} path="/my/mcp-connections" />

            {/* IDP: access enforced in-page (unauthorized users get Not Found, FR-013) */}
            <Route element={<Navigate to="/performance-review/idp/list" />} path="/performance-review/idp" />
            <Route element={<IdpList />} path="/performance-review/idp/list" />
            <Route element={<IdpDetail />} path="/performance-review/idp/detail/:id" />

            <Route element={<ProtectedRoute allowedFlag="reportView" redirectPath="/" />}>
              <Route element={<NumberOfEmployees />} path="/report/number-of-employees/:tab?" />
              <Route element={<PerformanceReviewReport />} path="/report/performance-review" />
              <Route element={<ManagerialAccountingReport />} path="/report/managerial-accounting/:tab?" />
              <Route element={<DemographicReport />} path="/report/demographic-report" />
              <Route element={<Navigate to="/report/number-of-employees" />} path="/report" />
            </Route>

            <Route element={<ProtectedRoute allowedFlag="settingView" redirectPath="/" />}>
              <Route element={<SettingAIAssistant />} path="/setting/ai/assistant" />
              <Route element={<SettingAIAssistantFormPage />} path="/setting/ai/assistant/add" />
              <Route element={<SettingAIAssistantFormPage />} path="/setting/ai/assistant/edit/:id" />
              <Route
                element={<ProtectedRoute allowedFlag={['performanceReviewStage', 'isSuperAdmin']} redirectPath="/" />}
              >
                <Route element={<SettingPerformanceReview />} path="/setting/performance-review/:tab?" />
              </Route>
              <Route element={<ProtectedRoute allowedFlag="settingNotificationView" redirectPath="/" />}>
                <Route element={<SettingNotification />} path="/setting/notification" />
              </Route>
              <Route element={<SettingGeneralList />} path="/setting/general" />
              <Route element={<OrgHierarchyRelationListPage />} path="/setting/org-hierarchy-relation" />
              <Route element={<OrgHierarchyRelationAddPage />} path="/setting/org-hierarchy-relation/add" />
              <Route element={<McpConnectionsSettingPage />} path="/setting/ai/mcp" />
              <Route element={<Navigate to="/setting/performance-review/stage" />} path="/setting" />
            </Route>
          </Route>
        </Route>
        <Route element={<AuthLayout />}>
          <Route element={<Login />} path="/login" />
          <Route element={<SignInCallback />} path="/auth/callback" />
          <Route
            element={
              <GoogleOfflineOAuthCallback
                redirectTo="/employees/list"
                redirectUri={import.meta.env.VITE_EMPLOYEE_CALLBACK_URL}
              />
            }
            path="/employees/callback"
          />
          <Route
            element={
              <GoogleOfflineOAuthCallback
                redirectTo="/performance-review/list"
                redirectUri={import.meta.env.VITE_PERFORMANCE_REVIEW_LIST_CALLBACK_URL}
              />
            }
            path="/performance-review/callback/list"
          />
          <Route
            element={
              <GoogleOfflineOAuthCallback
                redirectTo="/performance-review/results"
                redirectUri={import.meta.env.VITE_PERFORMANCE_REVIEW_RESULTS_CALLBACK_URL}
              />
            }
            path="/performance-review/callback/results"
          />
          <Route
            element={
              <GoogleOfflineOAuthCallback
                redirectTo="/probation-review/list"
                redirectUri={import.meta.env.VITE_PROBATION_REVIEW_LIST_CALLBACK_URL}
              />
            }
            path="/probation-review/callback/list"
          />
          <Route
            element={
              <GoogleOfflineOAuthCallback redirectTo="/report" redirectUri={import.meta.env.VITE_REPORT_CALLBACK_URL} />
            }
            path="/report/callback"
          />
        </Route>
      </Route>,
    ),
  );
